import { useState, useEffect, useRef, type CSSProperties } from "react";
import { showToast } from "../primitives";
import { useAuth } from "../../../lib/hooks/use-auth";
import { useCreateActivity } from "../../hooks/use-activities";
import { CHANNEL_OPTIONS, CHANNEL_LABEL } from "../../lib/activity-display";
import type { Activity, ActivityChannel, ActivityDirection } from "../../../lib/types";
import { modalOverlay, modalBox, fieldStyle, labelStyle, inputStyle } from "../scope/task-modal-shared";

interface LogActivityModalProps {
  open: boolean;
  onClose: () => void;
  /** The row this activity is primarily about. */
  entityTypeSlug: string;
  entityId: string;
  onLogged?: (activity: Activity) => void;
}

const DIRECTIONS: ActivityDirection[] = ["outbound", "inbound", "internal"];

/** Local datetime → ISO 8601 for occurred_at. */
function nowLocalInput(): string {
  const d = new Date();
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60_000);
  return local.toISOString().slice(0, 16);
}

/**
 * Sprint 14 — log an Activity against the current row. Pre-fills the row as the
 * primary `regarding` target; the actor defaults to the current user.
 */
export function LogActivityModal({ open, onClose, entityTypeSlug, entityId, onLogged }: LogActivityModalProps) {
  const { user } = useAuth();
  const [channel, setChannel] = useState<ActivityChannel>("note");
  const [direction, setDirection] = useState<ActivityDirection>("outbound");
  const [subject, setSubject] = useState("");
  const [summary, setSummary] = useState("");
  const [occurredAt, setOccurredAt] = useState(nowLocalInput());
  const subjectRef = useRef<HTMLInputElement>(null);
  const createActivity = useCreateActivity();

  useEffect(() => {
    if (open) {
      setChannel("note");
      setDirection("outbound");
      setSubject("");
      setSummary("");
      setOccurredAt(nowLocalInput());
      setTimeout(() => subjectRef.current?.focus(), 50);
    }
  }, [open]);

  if (!open) return null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!subject.trim()) return;
    createActivity.mutate(
      {
        subject: subject.trim(),
        channel,
        direction,
        occurred_at: new Date(occurredAt).toISOString(),
        actor: user?.id ? { kind: "user", id: user.id } : { kind: "system", id: "console" },
        regarding: [{ kind: "entity", type: entityTypeSlug, id: entityId, primary: true }],
        summary: summary.trim() || undefined,
      },
      {
        onSuccess: (activity) => {
          showToast("Activity logged");
          onLogged?.(activity);
          onClose();
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : "Something went wrong";
          showToast(`Couldn't log activity: ${msg}`, "error", true);
        },
      },
    );
  }

  return (
    <div style={modalOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalBox} role="dialog" aria-label="Log activity">
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <div style={iconBox}>
            <i className="ti ti-history" aria-hidden="true" />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)" }}>Log activity</div>
            <div style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>Record an interaction about {entityId}</div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ ...fieldStyle, flex: 1 }}>
              <label style={labelStyle}>Channel</label>
              <select style={inputStyle} value={channel} onChange={(e) => setChannel(e.target.value as ActivityChannel)}>
                {CHANNEL_OPTIONS.map((c) => <option key={c} value={c}>{CHANNEL_LABEL[c]}</option>)}
              </select>
            </div>
            <div style={{ ...fieldStyle, flex: 1 }}>
              <label style={labelStyle}>Direction</label>
              <select style={inputStyle} value={direction} onChange={(e) => setDirection(e.target.value as ActivityDirection)}>
                {DIRECTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Subject</label>
            <input
              ref={subjectRef}
              type="text"
              style={inputStyle}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. RE: Q3 proposal review"
              maxLength={200}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Summary <span style={{ color: "var(--color-text-tertiary)", fontWeight: 400 }}>(optional)</span></label>
            <textarea
              style={{ ...inputStyle, minHeight: 64, resize: "vertical" }}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="What happened?"
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Occurred at</label>
            <input
              type="datetime-local"
              style={inputStyle}
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
            />
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{ fontSize: 13, padding: "8px 14px" }}>Cancel</button>
            <button
              type="submit"
              data-variant="primary"
              disabled={!subject.trim() || createActivity.isPending}
              style={{ fontSize: 13, padding: "8px 14px" }}
            >
              {createActivity.isPending ? "Logging..." : "Log activity"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const iconBox: CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-secondary)",
  border: "var(--kuvio-border-default)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "var(--color-text-secondary)",
  fontSize: 20,
  flexShrink: 0,
};
